import React from "react";
import { useTheme } from "@material-ui/core/styles";
import {
	BarChart,
	Bar,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import { formatDayLabel } from "./format";

const axisProps = theme => ({
	stroke: theme.palette.text.secondary,
	tick: { fontSize: 12 },
});

// Tickets started per day.
export const DailyChart = ({ data, label }) => {
	const theme = useTheme();

	return (
		<ResponsiveContainer width="100%" height={260}>
			<BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
				<CartesianGrid strokeDasharray="3 3" vertical={false} />
				<XAxis
					dataKey="date"
					tickFormatter={formatDayLabel}
					minTickGap={16}
					{...axisProps(theme)}
				/>
				<YAxis allowDecimals={false} {...axisProps(theme)} />
				<Tooltip
					labelFormatter={formatDayLabel}
					formatter={value => [value, label]}
					cursor={{ fill: theme.palette.action.hover }}
				/>
				<Bar
					dataKey="tickets"
					fill={theme.palette.primary.main}
					radius={[4, 4, 0, 0]}
					maxBarSize={40}
				/>
			</BarChart>
		</ResponsiveContainer>
	);
};

// Tickets per queue, in each queue's own color.
export const QueueChart = ({ data, label }) => {
	const theme = useTheme();
	const height = Math.max(160, data.length * 44);

	return (
		<ResponsiveContainer width="100%" height={height}>
			<BarChart
				data={data}
				layout="vertical"
				margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
			>
				<CartesianGrid strokeDasharray="3 3" horizontal={false} />
				<XAxis type="number" allowDecimals={false} {...axisProps(theme)} />
				<YAxis
					type="category"
					dataKey="name"
					width={110}
					{...axisProps(theme)}
				/>
				<Tooltip
					formatter={value => [value, label]}
					cursor={{ fill: theme.palette.action.hover }}
				/>
				<Bar dataKey="tickets" radius={[0, 4, 4, 0]} maxBarSize={28}>
					{data.map(item => (
						<Cell key={item.key} fill={item.color} />
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};

// Conversations started in each hour of the day.
export const HourChart = ({ data, label }) => {
	const theme = useTheme();
	const rows = data.map((tickets, hour) => ({ hour: `${String(hour).padStart(2, "0")}h`, tickets }));

	return (
		<ResponsiveContainer width="100%" height={220}>
			<BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
				<CartesianGrid strokeDasharray="3 3" vertical={false} />
				<XAxis dataKey="hour" interval={2} {...axisProps(theme)} />
				<YAxis allowDecimals={false} {...axisProps(theme)} />
				<Tooltip
					formatter={value => [value, label]}
					cursor={{ fill: theme.palette.action.hover }}
				/>
				<Bar
					dataKey="tickets"
					fill={theme.palette.primary.main}
					radius={[3, 3, 0, 0]}
					maxBarSize={22}
				/>
			</BarChart>
		</ResponsiveContainer>
	);
};

// How many customers gave each grade (1 to 5).
export const RatingChart = ({ distribution, label }) => {
	const theme = useTheme();
	const rows = distribution.map((count, index) => ({ grade: String(index + 1), count }));

	return (
		<ResponsiveContainer width="100%" height={220}>
			<BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
				<CartesianGrid strokeDasharray="3 3" vertical={false} />
				<XAxis dataKey="grade" {...axisProps(theme)} />
				<YAxis allowDecimals={false} {...axisProps(theme)} />
				<Tooltip
					formatter={value => [value, label]}
					cursor={{ fill: theme.palette.action.hover }}
				/>
				<Bar
					dataKey="count"
					fill={theme.palette.primary.main}
					radius={[4, 4, 0, 0]}
					maxBarSize={44}
				/>
			</BarChart>
		</ResponsiveContainer>
	);
};

// Closed conversations by closing reason.
export const ReasonChart = ({ data, label }) => {
	const theme = useTheme();
	const height = Math.max(160, data.length * 40);

	return (
		<ResponsiveContainer width="100%" height={height}>
			<BarChart
				data={data}
				layout="vertical"
				margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
			>
				<CartesianGrid strokeDasharray="3 3" horizontal={false} />
				<XAxis type="number" allowDecimals={false} {...axisProps(theme)} />
				<YAxis type="category" dataKey="name" width={120} {...axisProps(theme)} />
				<Tooltip
					formatter={value => [value, label]}
					cursor={{ fill: theme.palette.action.hover }}
				/>
				<Bar
					dataKey="count"
					fill={theme.palette.primary.main}
					radius={[0, 4, 4, 0]}
					maxBarSize={26}
				/>
			</BarChart>
		</ResponsiveContainer>
	);
};
